import { iPaginationResponse } from "core/kangarooTypes/iPaginationResponse";
import { iUser } from "core/kangarooTypes/iUser";
import { addUser, getUsers, updateUser } from "core/Services/AdminServices";
import { Search, SortAsc, UserIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Skeleton from "react-loading-skeleton";
import { toast } from "react-toastify";
import FormInput from "shared/FormInput";
import { UserFormData } from "validationSchema/UserFormSchema";
import SortDialog from "./SortDialog";
import { getStatusColors } from "utils/statusColors";
import { useTranslation } from "react-i18next";

const SortingOrders = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [users, setUsers] = useState<iPaginationResponse<iUser>>();
  const [currentPage, setCurrentPage] = useState(1);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<iUser | null>(null);

  const {
    register,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      name: undefined,
      role: "driver",
    },
  });

  const fetchData = async (
    filters?: {
      search: string | undefined;
      role: string | undefined;
    },
    page: number = 1
  ) => {
    try {
      setIsLoading(true);
      const response = await getUsers(
        { search: filters?.search, role: "driver" },
        page
      );
      setUsers(response);
    } catch (error) {
      toast.error(t("sortingOrders.failedToLoadDrivers"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData({
      role: "driver",
      search: undefined,
    });
  }, []);

  useEffect(() => {
    const subscription = watch((values) => {
      fetchData({
        search: values.name,
        role: "driver",
      });
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchData(
      {
        search: watch("name"),
        role: "driver",
      },
      page
    );
  };

  const handleSort = (user: iUser) => {
    setSelectedUser(user);
    setIsDialogOpen(true);
  };

  const handleFormSubmit = async (data: UserFormData) => {
    try {
      // Ensure role is always driver
      const driverData = { ...data, role: "driver" };

      if (selectedUser) {
        await updateUser(driverData, selectedUser.id);
        toast.success(t("sortingOrders.driverUpdatedSuccess"));
      } else {
        await addUser(driverData);
        toast.success(t("sortingOrders.driverAddedSuccess"));
      }

      fetchData(
        {
          search: watch("name"),
          role: "driver",
        },
        currentPage
      );
    } catch (error) {
      toast.error(
        selectedUser
          ? t("sortingOrders.failedToUpdateDriver")
          : t("sortingOrders.failedToAddDriver")
      );
    }
  };

  return (
    <div className="flex gap-4 flex-col p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Driver Management Container */}
      <div className="bg-white rounded-md shadow-sm p-4 sm:p-6 flex gap-4 flex-col">
        {/* Search Row */}
        <div className="flex flex-col md:flex-row justify-between gap-4">
          {/* Search Input */}
          <div className="w-full md:w-1/2 lg:w-1/3">
            <FormInput
              name="name"
              placeholder={t("sortingOrders.searchByName")}
              register={register}
              errors={errors}
              leftIcon={<Search size={20} />}
            />
          </div>
        </div>

        {/* Driver Table */}
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full min-w-[800px]">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left p-3 w-1/5 font-medium text-xs tracking-wider text-gray-500 uppercase rounded-tl-lg">
                  {t("sortingOrders.driver")}
                </th>
                <th className="text-left p-3 w-2/5 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("sortingOrders.email")}
                </th>
                <th className="text-left p-3 w-1/5 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("sortingOrders.phoneNumber")}
                </th>
                <th className="text-left p-3 w-1/5 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("sortingOrders.status")}
                </th>
                <th className="text-right p-3 w-1/5 font-medium text-xs tracking-wider text-gray-500 uppercase rounded-tr-lg">
                  {t("sortingOrders.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                // Skeleton loading for table rows
                Array(5)
                  .fill(0)
                  .map((_, index) => (
                    <tr key={index}>
                      <td className="py-4 px-3">
                        <div className="flex items-center">
                          <Skeleton
                            circle
                            width={32}
                            height={32}
                            className="mr-3"
                          />
                          <Skeleton width={120} />
                        </div>
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={180} />
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={80} />
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={60} height={24} borderRadius={12} />
                      </td>
                      <td className="py-4 px-3 text-right">
                        <div className="flex justify-end gap-2">
                          <Skeleton width={20} height={20} />
                          <Skeleton width={20} height={20} />
                        </div>
                      </td>
                    </tr>
                  ))
              ) : users?.data && users.data.length > 0 ? (
                users.data.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="py-4 px-3">
                      <div className="flex items-center">
                        <div className="bg-gray-200 w-8 h-8 rounded-full flex items-center justify-center mr-3">
                          {user.id === 1 ? (
                            <UserIcon className="w-4 h-4" />
                          ) : (
                            <div className="text-xs font-medium">
                              {user.first_name.charAt(0)}
                            </div>
                          )}
                        </div>
                        <span className="font-medium text-sm">
                          {user.first_name} {user.last_name}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-3 text-gray-600 text-sm">
                      {user.email}
                    </td>
                    <td className="py-4 px-3 text-gray-600 text-sm">
                      {user.phone_number}
                    </td>
                    <td className="py-4 px-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs
                                             ${
                                               getStatusColors(
                                                 user.active
                                                   ? "active"
                                                   : "deactivated"
                                               ).bg
                                             }
                                            ${
                                              getStatusColors(
                                                user.active
                                                  ? "active"
                                                  : "deactivated"
                                              ).text
                                            }
                                             `}
                      >
                        {user.active
                          ? t("sortingOrders.active")
                          : t("sortingOrders.deactivated")}
                      </span>
                    </td>
                    <td className="py-4 px-3 text-right">
                      <div className="flex justify-start gap-2">
                        <button
                          className="text-gray-500 hover:text-blue-600 transition-colors p-1 rounded hover:bg-gray-100"
                          onClick={() => handleSort(user)}
                        >
                          <SortAsc className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    {t("sortingOrders.noDriversFound")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!isLoading && users?.meta && (
          <div className="bg-white px-4 py-3 flex flex-col sm:flex-row items-center justify-between border-t border-gray-200 sm:px-6 space-y-4 sm:space-y-0">
            <div className="text-sm text-gray-500">
              {users.data && users.data.length > 0 ? (
                <>
                  {t("sortingOrders.showing")} {users.meta.from}-{users.meta.to}{" "}
                  {t("sortingOrders.of")} {users.meta.total}{" "}
                  {t("sortingOrders.entries")}
                </>
              ) : (
                <>0 {t("sortingOrders.entries")}</>
              )}
            </div>
            {users.data && users.data.length > 0 && (
              <div className="flex space-x-1">
                <button
                  className="px-3 py-1 border rounded text-sm disabled:opacity-50 hover:bg-gray-50 transition-colors"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  {t("sortingOrders.previous")}
                </button>
                <button className="px-3 py-1 border rounded text-sm bg-blue-600 text-white">
                  {currentPage}
                </button>
                <button
                  className="px-3 py-1 border rounded text-sm disabled:opacity-50 hover:bg-gray-50 transition-colors"
                  disabled={currentPage === users.meta.last_page}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  {t("sortingOrders.next")}
                </button>
              </div>
            )}
          </div>
        )}
        <SortDialog
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          driverId={selectedUser?.id.toString() || ""}
        />
      </div>
    </div>
  );
};

export default SortingOrders;
